import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import config from "./config";

// Enums con valores numéricos
enum Price {
  Gratis = 0,
  Economico = 1,
  Moderado = 2,
  Costoso = 3,
}

enum PlaceType {
  Plaza = 0,
  ParqueTematico = 1,
  Campo = 2,
  JuegosNiños = 3,
  CamasElasticas = 4,
  ParqueAcuatico = 5,
  Museo = 6,
  Granja = 7,
  ParqueNatural = 8,
  EscapeRooms = 9,
  Restaurante = 10,
  Zoologicos = 11,
  Cine = 12,
  Arcade = 13,
  Shopping = 14,
}

const EditPlace = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [place, setPlace] = useState({
    id: "",
    name: "",
    description: "",
    lat: 0,
    longi: 0,
    price: Price.Economico,
    placeType: PlaceType.Plaza,
    webSite: "",
    direccion: "",
    calificacion: 0,
    urlGoogle: "",
    horarios: [] as string[],
    fotos: [] as string[],
    aprobado: false,
  });

  // Cargar datos existentes al montar el componente
  useEffect(() => {
    const fetchPlace = async () => {
      try {
        const response = await fetch(`${config.backendUrl}/api/Places/${id}`);
        if (!response.ok) {
          throw new Error("Error al cargar el lugar");
        }
        const data = await response.json();
        setPlace(data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlace();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPlace((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;
    setPlace((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  // Manejar cambios en horarios
  const handleHorarioChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newHorarios = [...place.horarios];
    newHorarios[index] = e.target.value;
    setPlace((prev) => ({
      ...prev,
      horarios: newHorarios,
    }));
  };

  const addHorario = () => {
    setPlace((prev) => ({
      ...prev,
      horarios: [...prev.horarios, ""],
    }));
  };

  const removeHorario = (index: number) => {
    const newHorarios = place.horarios.filter((_, i) => i !== index);
    setPlace((prev) => ({
      ...prev,
      horarios: newHorarios,
    }));
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${config.backendUrl}/api/Places/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(place),
      });

      if (response.ok) {
        alert("Cambios guardados exitosamente");
        navigate(-1); // Volver a la página anterior
      } else {
        alert("Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error al guardar los cambios");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600, margin: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Editar Lugar
      </Typography>

      {/* Nombre */}
      <TextField
        fullWidth
        label="Nombre"
        name="name"
        value={place.name}
        onChange={handleChange}
        margin="normal"
        required
      />
      
      {/* Descripción */}
      <TextField
        fullWidth
        label="Descripción"
        name="description"
        value={place.description}
        onChange={handleChange}
        margin="normal"
        multiline
        rows={4}
      />

      {/* Latitud y Longitud */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          fullWidth
          label="Latitud"
          name="lat"
          type="number"
          value={place.lat}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Longitud"
          name="longi"
          type="number"
          value={place.longi}
          onChange={handleChange}
          margin="normal"
        />
      </Box>

      {/* Precio */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Precio</InputLabel>
        <Select
          name="price"
          value={place.price}
          onChange={handleSelectChange}
          label="Precio"
        >
          {Object.values(Price)
            .filter((value) => typeof value === "number")
            .map((price) => (
              <MenuItem key={price} value={price}>
                {Price[price]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Tipo de Lugar */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Tipo de Lugar</InputLabel>
        <Select
          name="placeType"
          value={place.placeType}
          onChange={handleSelectChange}
          label="Tipo de Lugar"
        >
          {Object.values(PlaceType)
            .filter((value) => typeof value === "number")
            .map((type) => (
              <MenuItem key={type} value={type}>
                {PlaceType[type]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Sitio Web */}
      <TextField
        fullWidth
        label="Sitio Web"
        name="webSite"
        value={place.webSite}
        onChange={handleChange}
        margin="normal"
      />

      {/* Dirección */}
      <TextField
        fullWidth
        label="Dirección"
        name="direccion"
        value={place.direccion}
        onChange={handleChange}
        margin="normal"
      />

      {/* Calificación */}
      <TextField
        fullWidth
        label="Calificación"
        name="calificacion"
        type="number"
        value={place.calificacion}
        onChange={handleChange}
        margin="normal"
        inputProps={{ min: 0, max: 5 }}
      />

      {/* URL de Google */}
      <TextField
        fullWidth
        label="URL de Google"
        name="urlGoogle"
        value={place.urlGoogle}
        onChange={handleChange}
        margin="normal"
      />

      {/* Horarios */}
      <Typography variant="h6" gutterBottom>
        Horarios
      </Typography>
      {place.horarios.map((horario, index) => (
        <Box key={index} sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
          <TextField
            fullWidth
            value={horario}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleHorarioChange(e, index)}
            margin="normal"
          />
          <Button onClick={() => removeHorario(index)} color="error">
            Eliminar
          </Button>
        </Box>
      ))}
      <Button onClick={addHorario} variant="outlined" sx={{ mt: 1 }}>
        Agregar Horario
      </Button>

      {/* Fotos existentes */}
      {place.fotos.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fotos existentes
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {place.fotos.map((foto, index) => (
              <Box key={index} sx={{ position: "relative" }}>
                <img
                  src={`${config.backendUrl}${foto}`}
                  alt={`Foto ${index + 1}`}
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Botones */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained">
          Guardar Cambios
        </Button>
      </Box>
    </Box>
  );
};

export default EditPlace;